import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Global scroll-position memory for the whole app.
 *
 * It records the scroll offset of every scrollable element (and the window)
 * while the user scrolls, keyed by the current history entry (`location.key`).
 * When the user navigates back/forward to a previously visited entry, the saved
 * offsets are restored — so pressing "back" keeps the list exactly where it was
 * instead of jumping to the top. Works on every page automatically; no per-page
 * wiring needed.
 */

// key: `${location.key}` -> { [elementPath]: scrollTop }
// Kept in-memory for the lifetime of the tab (a full reload starts fresh, which
// matches how react-router regenerates history keys on reload anyway).
const store = new Map<string, Record<string, number>>()

const WINDOW_PATH = '@window'

// A stable-ish signature of an element based on its position in the DOM tree.
// The same page re-rendered after navigating back produces the same path, so we
// can match a saved offset to the right element without any per-page setup.
const pathOf = (el: Element): string => {
  const parts: number[] = []
  let node: Element | null = el
  while (node && node !== document.body && node.parentElement) {
    const parent = node.parentElement
    parts.unshift(Array.prototype.indexOf.call(parent.children, node))
    node = parent
  }
  return parts.join('/')
}

const elByPath = (path: string): HTMLElement | null => {
  if (path === '') return document.body
  let node: Element | undefined = document.body
  for (const idx of path.split('/').map(Number)) {
    node = node?.children[idx]
    if (!node) return null
  }
  return (node as HTMLElement) ?? null
}

const isDocumentScroll = (target: EventTarget | null) =>
  target === document ||
  target === document.documentElement ||
  target === document.body

export function useScrollRestoration() {
  const { key } = useLocation()

  // Continuously record scroll positions for the current history entry.
  useEffect(() => {
    let frame = 0
    const onScroll = (e: Event) => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const bucket = store.get(key) ?? {}
        if (isDocumentScroll(e.target)) {
          bucket[WINDOW_PATH] = window.scrollY
        } else if (e.target instanceof HTMLElement) {
          bucket[pathOf(e.target)] = e.target.scrollTop
        }
        store.set(key, bucket)
      })
    }
    // Capture phase so we catch scroll on any nested container (scroll doesn't bubble).
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('scroll', onScroll, true)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [key])

  // Restore the saved positions when returning to this history entry.
  useEffect(() => {
    const bucket = store.get(key)
    if (!bucket) return

    let raf = 0
    let attempts = 0
    const restore = () => {
      attempts += 1
      let pending = false

      for (const [path, top] of Object.entries(bucket)) {
        if (path === WINDOW_PATH) {
          const max =
            document.documentElement.scrollHeight - window.innerHeight
          window.scrollTo(0, Math.min(top, Math.max(max, 0)))
          if (max < top) pending = true
          continue
        }
        const el = elByPath(path)
        if (!el) {
          pending = true
          continue
        }
        if (Math.abs(el.scrollTop - top) > 1) el.scrollTop = top
        // Content may still be loading and shorter than the saved offset.
        if (el.scrollHeight - el.clientHeight < top) pending = true
      }

      // Retry across a few frames while async content finishes rendering.
      if (pending && attempts < 60) {
        raf = requestAnimationFrame(restore)
      }
    }
    raf = requestAnimationFrame(restore)
    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [key])
}
